import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";

export interface DailyUsage {
  configured: boolean; used: number; remaining: number; limit: number;
  timezone: string | null; pendingTimezone: string | null; resetAt: string | null;
}
function deviceTimezone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone; }
  catch { return ""; }
}
export default function SmsDailySettings({ isAgent = false, id = "sms-daily-settings" }: { isAgent?: boolean; id?: string | null }) {
  const queryClient = useQueryClient();
  const [timezone, setTimezone] = useState(deviceTimezone);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const draftIsDirty = useRef(false);
  const serverTimezone = useRef<string | null>(null);
  const { data, isLoading, isError } = useQuery<DailyUsage>({
    queryKey: ["/api/sms-daily-usage"], refetchInterval: 60_000, staleTime: 15_000,
  });
  const confirmedTimezone = data?.configured && data.timezone
    ? data.pendingTimezone ?? data.timezone
    : null;
  useEffect(() => {
    if (confirmedTimezone && confirmedTimezone !== serverTimezone.current) {
      serverTimezone.current = confirmedTimezone;
      if (!draftIsDirty.current) setTimezone(confirmedTimezone);
    }
  }, [confirmedTimezone]);
  const zones = [...new Set([timezone, data?.timezone, data?.pendingTimezone,
    deviceTimezone(), ...(typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") :
      ["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "Europe/London", "Australia/Sydney"]),
    "UTC"].filter((zone): zone is string => !!zone))].sort();
  const save = async () => {
    setSaving(true); setNotice("");
    try {
      const response = await apiRequest("/api/sms-home-timezone", {
        method: "PUT", body: { timezone, confirmed: true },
      });
      const result: DailyUsage = await response.json();
      const confirmed = result.configured && result.timezone
        ? result.pendingTimezone ?? result.timezone
        : timezone;
      serverTimezone.current = confirmed;
      draftIsDirty.current = false;
      setTimezone(confirmed);
      queryClient.setQueryData(["/api/sms-daily-usage"], result);
      await queryClient.invalidateQueries({ queryKey: ["/api/sms-usage"] });
      setNotice(result.pendingTimezone ? "Time zone change saved for the displayed reset. Your allowance has not reopened." :
        "Home time zone saved. Existing usage is retained; your next reset is shown above.");
    } catch (error) { setNotice(error instanceof Error ? error.message : "Could not save your home time zone."); }
    finally { setSaving(false); }
  };
  const reset = data?.resetAt && data.timezone ? new Date(data.resetAt).toLocaleString(undefined, {
    timeZone: data.pendingTimezone ?? data.timezone, timeZoneName: "short",
  }) : null;
  return <section id={id ?? undefined} className="rounded-2xl border border-slate-700 p-4 space-y-3" aria-label="Daily SMS limit">
    <h2 className="text-sm font-bold text-white">Daily SMS alerts</h2>
    {data ? data.configured ? <>
      <p className="text-sm text-slate-300">{data.used} / {data.limit} texts used · {data.remaining} remaining</p>
      <p className="text-xs text-slate-400">Next reset: {reset}. Home time zone: {data.timezone}.</p>
      {data.pendingTimezone && <p className="text-xs text-amber-300">Pending home time zone: {data.pendingTimezone}. Changing zones never resets an allowance early; the current window may be extended.</p>}
      {data.remaining === 0 && <p className="text-xs text-amber-300">SMS is paused until the daily reset. Selected email and push continue.</p>}
    </> : <p className="text-xs text-amber-300">Confirm your home time zone to enable automated SMS alerts. Email and push can continue without it.</p>
      : <p className="text-xs text-slate-400" role={isError ? "alert" : undefined}>{isLoading ? "Loading daily usage..." : "Daily usage is temporarily unavailable. Try again shortly."}</p>}
    {isError && <button type="button" onClick={() => queryClient.invalidateQueries({ queryKey: ["/api/sms-daily-usage"] })}
      className="text-xs font-semibold text-emerald-300 underline underline-offset-2">Retry daily usage</button>}
     <p className="text-xs text-slate-400">Up to {isAgent ? 5 : data?.limit ?? 3} automated alert texts per account each day. Each matching alert can text at most once per day. The Agent allowance resets at 00:01 in your home time zone, regardless of the beach’s time zone.</p>
    <div className="flex flex-wrap items-end gap-2">
      <label className="flex-1 min-w-0 text-xs text-slate-300">Home time zone
         <select value={timezone} onChange={event => {
           const nextTimezone = event.target.value;
           draftIsDirty.current = nextTimezone !== confirmedTimezone;
           setTimezone(nextTimezone);
         }}
          className="block w-full mt-1 rounded-lg border border-slate-600 bg-slate-900 p-2 text-white"
          disabled={!data || saving || isError}>
          {zones.map(zone => <option key={zone} value={zone}>{zone.replace(/_/g, " ")}</option>)}
        </select>
      </label>
       {!data?.configured && !isLoading && !isError && (
         <p className="w-full text-[11px] text-amber-300">Suggested from this device: {deviceTimezone() || "not available"}. This is only a suggestion; choose your home zone and confirm it below.</p>
       )}
      <button type="button" onClick={save} disabled={!data || !timezone || saving || isError}
        className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-bold text-white disabled:opacity-50">
        {saving ? "Saving..." : data?.configured ? "Save time zone" : "Confirm time zone"}
      </button>
    </div>
    {notice && <p role="status" className="text-xs text-slate-300">{notice}</p>}
    <p className="text-xs text-slate-400">Changing or cancelling a time zone change can postpone the next reset, but never reopens a used allowance early.</p>
    <p className="text-xs text-slate-400">Daily reports, Forecast Outlook and alert tests count too. Pending or uncertain sends count conservatively. The monthly segment limit still applies; Agent chat and phone verification are separate.</p>
  </section>;
}
